import {useState,useEffect} from 'react'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import InputGroup from 'react-bootstrap/InputGroup'
import Tooltip from 'react-bootstrap/Tooltip'
import Button from 'react-bootstrap/Button'
import Spinner from 'react-bootstrap/Spinner'
import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'
import {useSelector,useDispatch} from 'react-redux'
import useCurrencySettings from '../../../hooks/useCurrencySettings'
import useStripePromise from '../../../hooks/useStripeAccount'
import useS3DirectUpload from '../../../hooks/useS3DirectUpload'
import LocaleCurrency from '../../LocaleCurrency/LocaleCurrency'
import CenteredLoader from '../../CenteredLoader/CenteredLoader'
import { envAwarePost } from "../../../util/util"
import {
	usePackage,
	usePackages,
	usePackageCart,
	usePurchaseItemForms,
	useCreatePurchaseForm,
	useRemovePurchaseForm,
	usePurchaseItemsForms,
} from '../hooks'
import {usePrograms} from '../../programs/hooks'
import useCurrentUser from '../../../hooks/useCurrentUser'
import useApiStatusDispatch from '../../../hooks/useApiStatusDispatch'
import { selectCurrencyById } from '../../settings/settingsSlice'
import {getCurrencySymbol} from '../../../util/intl'
import {
	selectPackageById,
	sortPackages,
	toggleActivate,
	createPackage,
	deletePackage,
	editPackage,
	setEditor
} from './packagesSlice'
import { Elements, ElementsConsumer } from '@stripe/react-stripe-js'
import styles from '../FinancialsDashboard.module.css'
import PurchaseModal from "../PurchaseModal"
import {useHistory} from 'react-router-dom'
import DiscussResourceButton from '../../DiscussResourceButton/DiscussResourceButton'
import Select from 'react-select'

import { DndProvider,useDrag,useDrop } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'

const PACKAGE = 'PACKAGE'

const CartItem = ({id,qty}) => {
	const {updateCartItem} = usePackageCart()
	const pkg = usePackage(id)
	const [localQty,setLocalQty] = useState(qty)
	const handleUpdateQty = ()=>{
		updateCartItem({
			qty:Math.abs(parseInt(localQty)),
			id:pkg.package_id,
			currencyId:pkg.presentment_currency_id
		})
	}
	return (
		<div>
			<strong>Package Cart</strong>
			<hr className="my-1"/>
			<div className="d-flex">
				<div>
					<div>{pkg.package_name}</div>
					<div><small>{pkg.package_notes}</small></div>
				</div>
				<div className="ml-auto">
					<InputGroup size="sm">
						<Form.Control 
							onChange={ e => { setLocalQty(Math.abs(parseInt(e.target.value))) } }
							placeholder="qty"
							type="number"
							value={localQty}
						/>
						<InputGroup.Append>
							<Button 
								onClick={handleUpdateQty}
								variant="outline-primary"
							>
								Update
							</Button>
						</InputGroup.Append>
					</InputGroup>
				</div>
			</div>
		</div>
	)
}

const Cart = ()=>{
	const {
		setShow,
		idQtyMap,
		priceTotal,
		totalItems,
		sessionsTotal,
		currencyId,
		clearCart,
		grandTotal,
		taxAmount
	} = usePackageCart()
	const stripeAccount = useSelector(state => state.users.current_user.trainer_stripe_account)
	const stripePromise = useStripePromise(stripeAccount)
	const ids = Object.keys(idQtyMap)
	//select all packages being purchased and check if they have associated programs and
	//add the program ids to the meta so that the purchase success can forward to the 
	//assignment modal
	const packageMap = useSelector(state=>state.packages.entities)
	const packages = useSelector(state=>ids.map(id=>packageMap[id]))
	const {forms} = usePurchaseItemsForms('package',ids)
	const programs = packages.reduce(
		(carry,pkg)=>[...carry,...pkg.programs],
		[]
	)

	const {ISO} = useSelector(state=>state.settings.acceptedCurrencies.entities[currencyId])

	const clientUserId = useSelector(
		state => state.users.current_user.user_id
	)
	const clientName = useSelector(
		state => state.users.current_user.user_name
	)
	const trainerId = useSelector(
		state => state.users.current_user.bio_data.user_trainer_id
	)

	const purchaseDescription = `Package(s): ${Object.entries(idQtyMap)
		.map(([id,qty])=>`${qty} ${packageMap[id].package_name}`)
		.join(', ')
	}`

	const getPaymentIntent = () => {
		const payload = {
			action: 'getPaymentIntent',
			paymentAmount: grandTotal*100,
			clientEmail: clientUserId,
			trainer_id: trainerId,
			//these clients are logged thus we should not 
			//create a prospective client from their info
			create_prospect:0,
			numSessions: sessionsTotal,
			clientName: clientName,
			paymentType: "packages",
			purchase_description:purchaseDescription,
			package_currency_id:currencyId,
		}
		return envAwarePost("api/purchases/trainer_page/", payload)
	}

	const [clientSecret,setClientSecret] = useState(null)
	useEffect(async()=>{
		const res = await getPaymentIntent()
		setClientSecret(res.data)
	},[])

	const meta = {
		package_currency_id:currencyId,
		programs,
		packageIds:ids,
		forms
	}

	const [showPurchaseModal,setShowPurchaseModal] = useState(false)
	if ( totalItems === 0 ) return (
		<div className="d-flex align-items-stretch justify-content-center p-5">
			Cart is empty
		</div>
	)
	return (
		<div>
			{Object.entries(idQtyMap).map(
				([id,qty])=>(
					<CartItem id={id} qty={qty}/>
				)
			)}
			<div className="d-flex justify-content-end">
				<table>
					<tbody>
						<tr>
							<td className="pr-2">
								<strong>Price:</strong>
							</td>
							<td align="right">
								<LocaleCurrency amount={priceTotal.toFixed(2)} ISO={ISO} />
							</td>
						</tr>
						<tr>
							<td className="pr-2">
								<strong>Tax:</strong>
							</td>
							<td align="right">
								<LocaleCurrency amount={taxAmount.toFixed(2)} ISO={ISO} />
							</td>
						</tr>
						<tr>
							<td className="pr-2">
								<strong>Total:</strong>
							</td>
							<td align="right">
								<LocaleCurrency amount={grandTotal.toFixed(2)} ISO={ISO} />
							</td>
						</tr>
					</tbody>
				</table>
			</div>
			<div className="text-right">
				<Button onClick={()=>{ setShowPurchaseModal(true) }} variant="outline-success" size="sm">
					Checkout
				</Button>
			</div>
			{(Boolean(stripePromise)&&Boolean(clientSecret))&&
				<Elements stripe={stripePromise} options={{
					clientSecret
				}}>
					<ElementsConsumer>
						{({ elements, stripe }) => (
							<PurchaseModal
								meta={meta}
								totalPurchase={grandTotal}
								totalSessions={sessionsTotal}
								paymentType={"packages"}
								show={showPurchaseModal}
								toggle={() => {
									setShowPurchaseModal(!showPurchaseModal)
								}}
								elements={elements}
								stripe={stripe}
								onSuccess={()=>{
									clearCart()
								}}
							/>
						)}
					</ElementsConsumer>
				</Elements>
			}
		</div>
	)
}

const ReorderModalPlaceholder = ({idx})=>{
	const dispatch = useDispatch()
	const [{canDrop,isOver}, drop] = useDrop(
		monitor => ({
			accept:'PACKAGE',
			drop: item => {
				const srcIdx = item.sort_order
				const tgtIdx = idx
				console.log(srcIdx,tgtIdx)
				dispatch(sortPackages({
					srcIdx,
					tgtIdx,
				}))
			},
			collect: monitor => {
				const item = monitor.getItem()
				const canDrop = true
				return ({
					isOver: monitor.isOver(),
					canDrop,
				})
			}
		}),
	)
	return (
		<div 
			ref={drop} 
			style={{
				height:isOver&&canDrop?'18px':'4px',
				background:isOver&&canDrop?'blue':'transparent',
				transition:'0.1s',
				position:'relative'
			}}
		>
			<div
				style={{
					position:'absolute',
					height:isOver&&canDrop?'38px':'24px',
					width:'100%',
					top:'50%',left:0,
					transform:'translateY(-50%)'
				}}
			/>
		</div>
	)
}

const ReorderModalPackage = ({pkg})=>{
	const [{ isDragging }, drag] = useDrag(() => ({
		type: 'PACKAGE',
		item:pkg,
		collect: (monitor) => ({
			isDragging: Boolean(monitor.isDragging())
		})
	}))
	return (
		<div ref={drag} className="w-100 bg-white py-1 px-3 border rounded">
			<i className="fa fa-grip-vertical mr-1" style={{cursor:'grab'}}/>
			{pkg.package_name}
		</div>
	)
}

const ReorderModal = ({show,onHide})=>{
	const {status,packages} = usePackages()
	return (
		<Modal show={show} onHide={onHide}>
			<Modal.Header>
				Re-order Packages
			</Modal.Header>
			<Modal.Body>
				<DndProvider backend={HTML5Backend}>
					<ReorderModalPlaceholder idx={0}/>
					{packages.map(
						(pkg,i) => <>
							<ReorderModalPackage key={pkg.package_id} pkg={pkg}/>
							<ReorderModalPlaceholder key={`${pkg.package_id}-${i}`} idx={i+1}/>
						</>
					)}
				</DndProvider>
			</Modal.Body>
		</Modal>
	)
}

const Package = ({pkg}) => {
	const user = useCurrentUser()
	const isClient = user.account_type === 'client'

	const {status:formsStatus,forms} = usePurchaseItemForms('package',pkg.package_id)

	const {idQtyMap,updateCartItem} = usePackageCart()
	const qty = Boolean(idQtyMap[pkg.package_id]) ? Number(idQtyMap[pkg.package_id]) : ''

	const [localQty,setLocalQty] = useState(qty)

	useEffect(()=>{
		setLocalQty(qty)
	},[qty])

	const dispatch = useDispatch()
	const {ISO} = useSelector(state=>state.settings.acceptedCurrencies.entities[pkg.presentment_currency_id])

	const handleToggleActive = ()=>{
		dispatch(toggleActivate({
			id:pkg.package_id,
			update:{is_active:Number(pkg.is_active^1)}
		}))
	}

	const handleEdit = ()=>{
		dispatch(setEditor({
			show:true,
			id:pkg.package_id
		}))
	}

	const handleDelete = ()=>{
		if(!window.confirm(`Are you sure you want to delete package: ${pkg.package_name}?`)) return
		dispatch(deletePackage(pkg.package_id))
	}

	const encodeName = (name) => {
		let joinedName = encodeURIComponent(name);
		return joinedName;
	}

	const handleCopy = (valueToCopy) => {
		navigator.clipboard.writeText(valueToCopy)
	}

	const handleCopyLink = ()=>{
		const {package_id:id,package_name:name} = pkg
		handleCopy(
			`https://www.fitsw.com/paymentsDashboard?id=${id}&package=${encodeName(name)}`
		)
	}

	const handleUpdateQty = ()=>{
		updateCartItem({
			qty:Math.abs(parseInt(localQty) || 1),
			id:pkg.package_id,
			currencyId:pkg.presentment_currency_id
		})
	}

	return (
		<div className="border rounded d-flex align-items-stretch overflow-hidden mb-2 bg-white">
			<div>
				{Boolean(pkg.img)&&
					<img 
						style={{
							width:'128px',
							height:'128px',
							objectFit:'cover'
						}} 
						src={pkg.img}
					/>
				}
			</div>
			<div className="p-1 w-100">
				<div className="d-flex justify-content-between w-100">
					<strong>{pkg.package_name}</strong>
					{!isClient&&
						<>
							<div className="d-flex align-items-center" style={{flex:'0 0 auto'}}>
								<small className="mr-1">activate</small>
								<Form.Switch 
									id={`package-active-toggle-${pkg.package_id}`}
									onChange={handleToggleActive} 
									checked={Boolean(pkg.is_active)}
								/>
							</div>
						</>
					}
				</div>
				<div><small>{pkg.package_notes}</small></div>
				<div><small>Session Credits: {pkg.session_credits}</small></div>
				<div><small>Price: <LocaleCurrency amount={pkg.package_price} ISO={ISO}/></small></div>
				{ pkg?.programs?.length > 0 &&
					<div>
						<small>
							Programs: {
								pkg.programs.map(
									p => p.programName
								).join(', ')
							}
						</small>
					</div>
				}
				
				<div>
					<small>
						Forms: {
							formsStatus==='idle'
							//if forms are loaded
							? (
								forms.length > 0
								? forms.map(
									f => f.form_title
								).join(', ')
								: 'No forms/waivers attached to this package'
							)
							//forms still loading
							: 'Loading forms...'
						}
					</small>
				</div>
				{!isClient&&
					<div>
						<small>
							Package Link: 
								<Button 
									onClick={handleCopyLink}
									variant="link"
									size="sm"
								>
									Copy Package Link
								</Button>
						</small>
					</div>
				}
				<div>
					{Boolean(pkg.is_active)&&
						<OverlayTrigger overlay={
							<Tooltip>Discuss this package in the chat</Tooltip>
						}>
							<span className="ml-auto">
								<DiscussResourceButton
									className="btn btn-sm btn-link text-info"
									name={pkg.package_name}
									id={pkg.package_id}
									type="package"
								>
									Discuss
								</DiscussResourceButton>
							</span>
						</OverlayTrigger>
					}
					{!isClient&&
						<>
							<Button onClick={handleEdit} variant="link" className="text-primary" size="sm">
								Edit
							</Button>
							<Button onClick={handleDelete} variant="link" className="text-danger" size="sm">
								Delete
							</Button>
						</>
					}
				</div>
				{isClient&&
					<InputGroup size="sm">
						<Form.Control 
							onChange={ e => { setLocalQty(Math.abs(parseInt(e.target.value))) } }
							placeholder="qty"
							type="number"
							value={localQty}
						/>
						<InputGroup.Append>
							<Button 
								onClick={handleUpdateQty}
								variant="outline-primary"
							>
								{Boolean(qty)?'Update':'Add'}
							</Button>
						</InputGroup.Append>
					</InputGroup>
				}
			</div>
		</div>
	)
}

const Editor = ()=>{
	const dispatch = useDispatch()
	const statusDispatch = useApiStatusDispatch()
	const {show,id} = useSelector(
		state => state.packages.editor
	)
	const pkg = useSelector(
		state => id && selectPackageById(state,id)
	)
	const isNew = !Boolean(id)
	const {status:programsStatus,programs} = usePrograms()

	const createPurchaseForm = useCreatePurchaseForm()
	const removePurchaseForm = useRemovePurchaseForm()

	const {processUpload} = useS3DirectUpload()

	const handleClose = ()=>{
		dispatch(setEditor({show:false,id:null}))
	}


	const {
		status:packageFormsStatus,
		forms:packageForms,
		allForms
	} = usePurchaseItemForms('package',id)


	const {
		acceptedCurrency: defaultAcceptedCurrency,
		acceptedCurrencies
	} = useCurrencySettings()

	const [currentImage,setCurrentImage] = useState('')
	const [removeCurrentImage,setRemoveCurrentImage] = useState(false)
	const [newImage,setNewImage] = useState(null)
	const [title,setTitle] = useState('')
	const [notes,setNotes] = useState('')
	const [sessionCredits,setSessionCredits] = useState('')
	const [price,setPrice] = useState('')
	const [acceptedCurrencyId,setAcceptedCurrencyId] = useState(defaultAcceptedCurrency.id)
	const [isActive,setIsActive] = useState(false)
	const [createClient,setCreateClient] = useState(false)
	const [selectedPrograms,setSelectedPrograms] = useState(
		pkg?.programs?.map(
			p => (
				{
					label:p.programName,
					value:p.programID
				}
			)//end map cb
		)//end map
	)

	const acceptedCurrency = useSelector(
		state => selectCurrencyById(state, acceptedCurrencyId)
	)

	const handleUpdateCurrency = e => {
		const currency_id = e.target.value
		setAcceptedCurrencyId(currency_id)
	}

	const reset = ()=>{
		setRemoveCurrentImage(false)
		setCurrentImage('')
		setNewImage(null)
		setTitle('')
		setNotes('')
		setSessionCredits('')
		setPrice('')
		setAcceptedCurrencyId(defaultAcceptedCurrency.id)
		setIsActive(false)
		setCreateClient(true)
		setSelectedPrograms([])
	}

	useEffect(()=>{
		if (id) {
			setIsActive(Boolean(pkg.is_active))
			setCurrentImage(pkg.img)
			setTitle(pkg.package_name)
			setNotes(pkg.package_notes)
			setPrice(pkg.package_price)
			setCreateClient(Boolean(pkg.create_client))
			setSessionCredits(pkg.session_credits)
			setAcceptedCurrencyId(pkg.presentment_currency_id)
			setSelectedPrograms(
				pkg.programs.map(
					p => (
						{
							label:p.programName,
							value:p.programID
						}
					)//end map cb
				)//end map)
			)
		} else {
			reset()
		}
	},[show,id])

	const [status,setStatus] = useState('idle')
	const handleSave = async ()=>{
		setStatus('pending')
		if (isNew) {
			const payload = {
				package_name:title,
				package_notes:notes,
				session_credits:sessionCredits,
				is_active:Number(isActive),
				create_client:Number(createClient),
				presentment_currency_id:acceptedCurrencyId,
				package_price:price,
				program_ids:selectedPrograms.map(p=>p.value)
			}
			if(Boolean(newImage)){
				const s3Res = await processUpload(newImage)
				payload.img = s3Res.payload.url
			}
			statusDispatch(createPackage(payload),'package','create')
			.then(res=>{
				setStatus('idle')
				reset()
				handleClose()
			})
		} else {
			const payload = {
				package_id:pkg.package_id,
				package_name:title,
				package_notes:notes,
				session_credits:sessionCredits,
				is_active:Number(isActive),
				create_client:Number(createClient),
				presentment_currency_id:acceptedCurrencyId,
				package_price:price,
				program_ids:selectedPrograms.map(p=>p.value)
			}
			if(Boolean(newImage)){
				const s3Res = await processUpload(newImage)
				payload.img = s3Res.payload.url
			}else if(removeCurrentImage){
				payload.img = ''
			}
			statusDispatch(editPackage(payload),'package','edit').then(res=>{
				setStatus('idle')
				reset()
				handleClose()
			})
		}
	}

	return (
		<Modal onHide={handleClose} show={show}>
			<Modal.Header closeButton>
				<Modal.Title>
					{isNew?'Create':'Edit'} Package
				</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{(Boolean(currentImage)&&!Boolean(newImage)&&!removeCurrentImage)&&
					<div>
						<img 
							className="rounded mb-2"
							src={currentImage} 
							style={{width:'100%',display:'block'}}
						/>
						<Button 
							onClick={()=>{ setRemoveCurrentImage(true) }}
							variant="link" 
							className="text-danger"
						>
							Remove Current Image
						</Button>
					</div>
				}
				{(Boolean(currentImage)&&!Boolean(newImage)&&removeCurrentImage)&&
					<Button 
						onClick={()=>{ setRemoveCurrentImage(false) }}
						variant="link" 
						className="text-success"
					>
						Restore Current Image
					</Button>
				}
				{Boolean(newImage)&&
					<div>
						<img 
							className="rounded mb-2"
							src={window.URL.createObjectURL(newImage)} 
							style={{width:'100%',display:'block'}}
						/>
						<Button 
							onClick={()=>{ setNewImage(null) }}
							variant="link" 
							className="text-danger"
						>
							{Boolean(currentImage)?'Use Previous Image':'Clear Image'}
						</Button>
					</div>
				}
				<Form.File
					onChange={ e => { setNewImage(e.target.files[0]) } }
					id="package-img-select"
					label="Package Image"
					custom
				/>
				<strong>Package Title</strong>
				<Form.Control
					onChange={e=>{ setTitle(e.target.value) }}
					value={title}
				/>
				<strong>Package Notes</strong>
				<Form.Control 
					onChange={e=>{ setNotes(e.target.value) }}
					value={notes}
					as="textarea"
				/>
				<strong>Linked Programs (Optional)</strong>
				<div>
					<Select
						onChange={setSelectedPrograms}
						options={
							programs.map(
								p => (
									{
										label:p.programName,
										value:p.programID
									}
								)//end map cb
							)//end map
						}//end options
						value={selectedPrograms}
						isMulti
					/>
				</div>
				<strong>Linked Forms/Waivers (Optional)</strong>
				<div>
					<Select 
						onChange={(_,payload)=>{
							switch(payload.action){
								case 'select-option':
									createPurchaseForm({
										itemType:'package',
										itemId:pkg.package_id,
										formId:payload.option.value
									})
									break;
								case 'remove-value':
									removePurchaseForm({
										itemType:'package',
										itemId:pkg.package_id,
										formId:payload.removedValue.value
									})
									break;
							}
						}}
						value={
							packageForms.map(
								form => ({
									value:form.form_id,
									label:form.form_title
								})
							)//end map
						}
						options={
							allForms.map(
								form => ({
									value:form.form_id,
									label:form.form_title
								})
							)//end map
						}//end options
						isMulti
					/>
				</div>
				<strong>Session Credits</strong>
				<Form.Control
					onChange={e=>{ setSessionCredits(e.target.value) }}
					value={sessionCredits}
				/>
				<strong>Package Price</strong>
				<div className="input-group">
					<Form.Control
						onChange={handleUpdateCurrency}
						className="input-group-prepend"
						value={acceptedCurrencyId}
						style={{ maxWidth: '100px' }}
						as="select"
					>
						{acceptedCurrencies.map( ac => (
							<option key={ac.id} value={ac.id}>
								{getCurrencySymbol(ac.ISO) === ac.ISO 
									? ac.ISO 
									: `${getCurrencySymbol(ac.ISO)} ${ac.ISO}`
								}
							</option>
						))}
					</Form.Control>
					<Form.Control
						style={{ padding: '.375rem .75rem', borderLeft: '1px solid #ced4da' }}
						placeholder={`Price in ${getCurrencySymbol(acceptedCurrency?.ISO)}`}
						onChange={ e => { setPrice(e.target.value) } }
						name="package_price"
						value={price}
						type="number"
						min="0"
					/>
				</div>
				<strong>Activate</strong>
				<Form.Switch
					onChange={ e => { setIsActive(e.target.checked) } }
					checked={isActive}
					id="create-edit-package-active-switch"
				/>
				<strong className="d-block">Create client on purchase</strong>
				<div className="d-flex align-items-center">
					<Form.Switch
						onChange={ e => { setCreateClient(e.target.checked) } }
						checked={createClient}
						id="create-client-package-switch"
					/>
					{
						createClient
						? <small className="text-success">(enabled)</small>
						: <small className="text-danger">(disabled)</small>
					}
				</div>
				<small className="d-block">
					<i className="fa fa-info-circle"/> When checked a person that purchases this package will automatically be added as a client. When unchecked they will be added as a prospective client and will not count towards your account totals.
				</small>
				<div className="text-right mt-2">
					<Button disabled={status==='pending'} onClick={handleSave} variant="outline-success">
						<span className="mr-1">Save</span>
						{status==='pending'&&<Spinner animation="border" size="sm"/>}
					</Button>
				</div>
			</Modal.Body>
		</Modal>
	)
}

const Packages = ()=>{
	const history = useHistory()
	const urlParams = new URLSearchParams(window.location.search)
	const filterId = parseInt(urlParams.get('package_id'))
	const {totalItems,show:showCart,setShow:setShowCart} = usePackageCart()
	const user = useCurrentUser()
	const isClient = user.account_type === 'client'
	const dispatch = useDispatch()
	const {status,packages} = usePackages()

	const [showReorderModal,setShowReorderModal] = useState(false)

	const statusDispatch = useApiStatusDispatch()

	const {
		acceptedCurrency: defaultAcceptedCurrency,
	} = useCurrencySettings()

	const [isAdding,setIsAdding] = useState(false)
	const handleAdd = () => { 
		setIsAdding(true)
		const payload = {
			package_name:'Untitled Package',
			package_notes:'',
			session_credits:0,
			is_active:0,
			presentment_currency_id:defaultAcceptedCurrency.id,
			package_price:0,
			program_ids:[],
			form_ids:[]
		}
		
		statusDispatch(createPackage(payload),'package','create')
		.then(action=>{
			setIsAdding(false)
			dispatch(setEditor({
				show:true,
				id:action.payload.data.package.package_id
			})) 
		})
		
	}

	const handleShowCart = ()=>{ setShowCart(true) }
	const handleHideCart = ()=>{ setShowCart(false) }

	return (
		<div style={{height:'100%',overflow:'auto'}}>
			<div className="text-right mb-2">
				{!isClient&&
					<>
						<Button 
							className="mr-1"
							disabled={isAdding}
							onClick={setShowReorderModal.bind(null,true)} 
							variant="outline-dark"
						>
							Reorder Packages
						</Button>
						<Button 
							disabled={isAdding}
							onClick={handleAdd} 
							variant="outline-success"
						>
							Add Package {isAdding&&<Spinner animation="border" size="sm"/>}
						</Button>
					</>
				}
				{showCart&&
					<Button 
						variant="outline-dark"
						onClick={handleHideCart} 
					>
						Back To Package List
					</Button>
				}
				{(isClient&&!showCart)&&
					<Button 
						variant="outline-primary"
						onClick={handleShowCart} 
						disabled={totalItems===0}
					>
						Cart <i className="fas fa-shopping-cart"/> {totalItems>0&&`(${totalItems})`}
					</Button>
				}
			</div>
			{showCart&&<Cart/>}
			{
				status === 'pending' 
				? <CenteredLoader/>
				: 
				(
					!showCart&&
					<div>
						{Boolean(filterId)&&
							<div className="mb-2">
								Viewing filtered list: 
								<Button 
									onClick={()=>{ 
										history.replace('/financials_dashboard/')
									}}
									variant="outline-danger" 
									className="ml-2"
									size="sm"
								>
									Clear Filters
								</Button>
							</div>
						}
						<DndProvider backend={HTML5Backend}>
							<div id={styles.Packages}>
								{packages.filter(p=>!Boolean(filterId)||p.package_id===filterId).map( pkg => <Package pkg={pkg} /> )}
							</div>
						</DndProvider>
					</div>
				)
			}
			<Editor/>
			<ReorderModal
				show={showReorderModal}
				onHide={setShowReorderModal.bind(null,false)}
			/>
		</div>
	)
}

export default Packages